import React, { useEffect, useState } from 'react';
import { CapsuleTabs, DatePicker, DotLoading, SpinLoading } from 'antd-mobile';
import dayjs, { Dayjs } from 'dayjs';
import styles from './index.module.scss';
import EnergyCost from './components/EnergyCost';
import EnergyUsage from './components/EnergyUsage';
import {
  EnergyCostResponse,
  EnergyUsageResponse,
  getEnergyCost,
  getEnergyUsage,
  TimeResolution,
} from '@/api/energyOverview';
import EnergyAnalysis from './components/EnergyAnalysis';

const EnergyOverview = () => {
  const [dimension, setDimension] = useState<TimeResolution>(TimeResolution.DAY);
  const [selectedYear, setSelectedYear] = useState<Dayjs>(dayjs());
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs());
  const [monthPickerVisible, setMonthPickerVisible] = useState(false);
  const [yearPickerVisible, setYearPickerVisible] = useState(false);
  const [energyCostData, setEnergyCostData] = useState<EnergyCostResponse>();
  const [energyUsageData, setEnergyUsageData] = useState<EnergyUsageResponse>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = '能源概览';
  }, []);

  // 切换维度时重置为当前时间
  useEffect(() => {
    if (dimension === TimeResolution.MONTH) {
      setSelectedYear(dayjs());
    } else {
      setSelectedMonth(dayjs());
    }
  }, [dimension]);

  // 获取能源数据
  const fetchEnergyData = async () => {
    setLoading(true);
    try {
      const fromTime =
        dimension === TimeResolution.MONTH
          ? selectedYear.startOf('year').valueOf()
          : selectedMonth.startOf('month').valueOf();
      const toTime =
        dimension === TimeResolution.MONTH
          ? selectedYear.endOf('year').valueOf()
          : selectedMonth.endOf('month').valueOf();

      const params = {
        fromTime,
        toTime,
        timeResolution: dimension,
      };

      // 并行请求成本和用量数据
      const [costRes, usageRes] = await Promise.all([getEnergyCost(params), getEnergyUsage(params)]);

      setEnergyCostData(costRes);
      setEnergyUsageData(usageRes);
    } catch (error) {
      console.error('获取能源数据失败', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnergyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimension, selectedYear, selectedMonth]);

  // 获取当前选择的时间显示文本
  const getTimeDisplay = () => {
    if (dimension === TimeResolution.MONTH) {
      return selectedYear.format('YYYY年');
    }
    return selectedMonth.format('YYYY年M月');
  };

  return (
    <div className={styles.container}>
      {/* 维度切换 + 时间选择 */}
      <div className={styles.header}>
        <CapsuleTabs activeKey={dimension} onChange={key => setDimension(key as string as TimeResolution)}>
          <CapsuleTabs.Tab key={TimeResolution.DAY} title="月" />
          <CapsuleTabs.Tab key={TimeResolution.MONTH} title="年" />
        </CapsuleTabs>
        {dimension === TimeResolution.MONTH ? (
          <DatePicker
            precision="year"
            value={selectedYear.toDate()}
            visible={yearPickerVisible}
            onClose={() => setYearPickerVisible(false)}
            onConfirm={val => {
              setSelectedYear(dayjs(val));
              setYearPickerVisible(false);
            }}
          >
            {value => (
              <div className={styles.timeSelector} onClick={() => setYearPickerVisible(true)}>
                <span>{value ? dayjs(value).format('YYYY年') : getTimeDisplay()}</span>
                <span className={styles.arrow}>▼</span>
              </div>
            )}
          </DatePicker>
        ) : (
          <DatePicker
            precision="month"
            visible={monthPickerVisible}
            value={selectedMonth.toDate()}
            onClose={() => setMonthPickerVisible(false)}
            onConfirm={val => {
              setSelectedMonth(dayjs(val));
              setMonthPickerVisible(false);
            }}
          >
            {value => (
              <div className={styles.timeSelector} onClick={() => setMonthPickerVisible(true)}>
                <span>{value ? dayjs(value).format('YYYY年M月') : getTimeDisplay()}</span>
                <span className={styles.arrow}>▼</span>
              </div>
            )}
          </DatePicker>
        )}
      </div>
      {/* Loading 状态 */}
      {loading && (
        <div className={styles.loadingContainer}>
          <DotLoading color="primary" />
          <span>加载中</span>
        </div>
      )}
      {/* 能源成本展示组件 */}
      {!loading && energyCostData && <EnergyCost energyCostData={energyCostData} dimension={dimension} />}
      {/* 能源用量展示组件 */}
      {!loading && energyUsageData && <EnergyUsage energyUsageData={energyUsageData} dimension={dimension} />}
      {/* 节能降耗分析 */}
      {!loading && <EnergyAnalysis />}
    </div>
  );
};

export default EnergyOverview;
